import { chatSchema } from './schema.js'

// Parse an array of message objects into a ProseMirror document.
// Each message { role, content, sequence, timestamp } becomes a `message` node.

export function parseMessages(messages) {
  const messageNodes = messages.map((msg) => {
    const blocks = parseContent(msg.content || '')
    return chatSchema.node('message', {
      role: msg.role || 'unknown',
      sequence: msg.sequence ?? 0,
      timestamp: msg.timestamp || null,
    }, blocks.length > 0 ? blocks : [chatSchema.node('paragraph')])
  })

  if (messageNodes.length === 0) {
    messageNodes.push(
      chatSchema.node('message', { role: 'system' }, [
        chatSchema.node('paragraph', null, [chatSchema.text('(empty session)')]),
      ])
    )
  }

  return chatSchema.node('doc', null, messageNodes)
}

// Simple markdown-ish parser. Handles:
// - Paragraphs (double newlines)
// - Code blocks (``` fences)
// - Bullet lists (lines starting with - or *)
// - Ordered lists (lines starting with 1. 2. etc.)
// - Inline: **bold**, *italic*, `code`, [text](url)

function parseContent(text) {
  const blocks = []
  const lines = text.split('\n')
  let i = 0

  while (i < lines.length) {
    const line = lines[i]

    // Code block fence
    if (line.trimStart().startsWith('```')) {
      const codeLines = []
      i++
      while (i < lines.length && !lines[i].trimStart().startsWith('```')) {
        codeLines.push(lines[i])
        i++
      }
      i++ // skip closing fence
      const codeText = codeLines.join('\n')
      blocks.push(chatSchema.node('code_block', null,
        codeText ? [chatSchema.text(codeText)] : []))
      continue
    }

    // Bullet list (- or *)
    if (/^[ \t]*[-*] /.test(line)) {
      const items = []
      while (i < lines.length && /^[ \t]*[-*] /.test(lines[i])) {
        const itemText = lines[i].replace(/^[ \t]*[-*] /, '')
        items.push(chatSchema.node('list_item', null, [
          chatSchema.node('paragraph', null, parseInline(itemText)),
        ]))
        i++
      }
      blocks.push(chatSchema.node('bullet_list', null, items))
      continue
    }

    // Ordered list (1. 2. etc.)
    if (/^[ \t]*\d+\. /.test(line)) {
      const items = []
      const startMatch = line.match(/^[ \t]*(\d+)\. /)
      const start = parseInt(startMatch[1]) || 1
      while (i < lines.length && /^[ \t]*\d+\. /.test(lines[i])) {
        const itemText = lines[i].replace(/^[ \t]*\d+\. /, '')
        items.push(chatSchema.node('list_item', null, [
          chatSchema.node('paragraph', null, parseInline(itemText)),
        ]))
        i++
      }
      blocks.push(chatSchema.node('ordered_list', { start }, items))
      continue
    }

    // Blank line — skip
    if (!line.trim()) {
      i++
      continue
    }

    // Regular paragraph — accumulate non-blank, non-special lines
    const paraLines = []
    while (
      i < lines.length &&
      lines[i].trim() &&
      !lines[i].trimStart().startsWith('```') &&
      !/^[ \t]*[-*] /.test(lines[i]) &&
      !/^[ \t]*\d+\. /.test(lines[i])
    ) {
      paraLines.push(lines[i])
      i++
    }
    const inline = parseInline(paraLines.join('\n'))
    blocks.push(chatSchema.node('paragraph', null,
      inline.length > 0 ? inline : []))
  }

  return blocks
}

// Parse inline markdown: **bold**, *italic*, `code`, [text](url)
function parseInline(text) {
  if (!text) return []

  const nodes = []
  // Regex matches: **bold**, *italic*, `code`, [text](url), or plain text
  const re = /(\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\))/g
  let lastIndex = 0
  let match

  while ((match = re.exec(text)) !== null) {
    // Text before match
    if (match.index > lastIndex) {
      nodes.push(chatSchema.text(text.slice(lastIndex, match.index)))
    }

    if (match[2]) {
      // **bold**
      nodes.push(chatSchema.text(match[2], [chatSchema.mark('bold')]))
    } else if (match[3]) {
      // *italic*
      nodes.push(chatSchema.text(match[3], [chatSchema.mark('italic')]))
    } else if (match[4]) {
      // `code`
      nodes.push(chatSchema.text(match[4], [chatSchema.mark('code')]))
    } else if (match[5]) {
      // [text](url)
      nodes.push(chatSchema.text(match[5], [
        chatSchema.mark('link', { href: match[6] }),
      ]))
    }

    lastIndex = match.index + match[0].length
  }

  // Trailing text
  if (lastIndex < text.length) {
    nodes.push(chatSchema.text(text.slice(lastIndex)))
  }

  return nodes
}
